import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Chip,
  Autocomplete,
} from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { GetPortalsDdlReportResult } from "../../../../services/api/types/reports/reports";

interface PortalSelectionProps {
  value: {
    portals: string[];
    groupResultsByPortal: boolean;
  };
  portalOptions: GetPortalsDdlReportResult[];
  onChange: (data: {
    portals?: string[];
    groupResultsByPortal?: boolean;
  }) => void;
  errors: Record<string, string>;
  isExpanded: boolean;
}

export const PortalSelection: React.FC<PortalSelectionProps> = ({
  value,
  portalOptions,
  onChange,
  errors,
  isExpanded,
}) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  // Create flat list of portals ordered parent, then children
  const { portalOptionsList, portalMap } = useMemo(() => {
    const list: string[] = [];
    const map = new Map<
      string,
      { name: string; isSubPortal: boolean; parentName?: string }
    >();

    portalOptions.forEach((portal) => {
      // Parent
      list.push(portal.portalUid);
      map.set(portal.portalUid, {
        name: portal.name,
        isSubPortal: false,
      });

      // Subportals
      portal.subPortals?.forEach((sub) => {
        list.push(sub.portalUid);
        map.set(sub.portalUid, {
          name: sub.name,
          isSubPortal: true,
          parentName: portal.name,
        });
      });
    });

    return { portalOptionsList: list, portalMap: map };
  }, [portalOptions]);

  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        borderRadius: "12px",
        backgroundColor: "white",
        border: "1px solid #E5E7EB",
        boxShadow: "none",
        "&:before": { display: "none" }, // remove default divider line
        "&:first-of-type": {
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
        },
        "&:not(:last-of-type)": {
          borderBottom: "1px solid #E5E7EB",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 2.5,
          py: 1.5,
          minHeight: 64,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            margin: 0,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <PeopleAltOutlinedIcon sx={{ color: "#7C3AED", fontSize: 24 }} />
          <Typography fontSize={18} fontWeight={500}>
            Portal selection
          </Typography>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 3, pb: 3 }}>
        <Box sx={{ borderTop: "1px solid #E5E7EB", pt: 2 }}>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Stack spacing={1} width="100%">
              <Typography variant="subtitle2">
                Select portal
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              </Typography>

              <Autocomplete
                multiple
                options={portalOptionsList}
                value={value.portals}
                size="small"
                onChange={(_, newValue) => onChange({ portals: newValue })}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => {
                  const portal = portalMap.get(option);
                  return portal?.name || option;
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                  },
                }}
                renderOption={(props, option) => {
                  const portal = portalMap.get(option);
                  const isSubPortal = portal?.isSubPortal || false;
                  const { key, ...optionProps } = props;

                  return (
                    <Box
                      component="li"
                      key={key}
                      {...optionProps}
                      sx={{
                        px: 2,
                        py: 1.2,
                        fontWeight: isSubPortal ? 400 : 600,
                        fontSize: isSubPortal ? 14 : 15,
                        ml: isSubPortal ? 2 : "",
                      }}
                    >
                      {portal?.name}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select portal"
                    required
                    error={Boolean(errors?.portals)}
                    helperText={errors?.portals}
                  />
                )}
                renderValue={(value, getTagProps) =>
                  value.map((option, index) => {
                    const portal = portalMap.get(option);
                    const displayLabel =
                      portal?.isSubPortal && portal?.parentName
                        ? `${portal.parentName} > ${portal.name}`
                        : portal?.name || option;
                    return (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={displayLabel}
                        sx={{
                          backgroundColor: "white",
                          padding: "14px 6px",
                          borderRadius: "6px",
                        }}
                      />
                    );
                  })
                }
                slotProps={{
                  popupIndicator: {
                    sx: { border: "none" },
                  },
                  clearIndicator: {
                    sx: { display: "none" },
                  },
                }}
              />
            </Stack>
          </Stack>

          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={value.groupResultsByPortal}
                onChange={(e) =>
                  onChange({ groupResultsByPortal: e.target.checked })
                }
              />
            }
            label="Group results by portal"
            labelPlacement="start"
            sx={{ mt: 3, ml: "2px", gap: 1.5 }}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
